import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { sanitizeSkillName } from '../../../utils/skillsUtils';

const WorkExperienceTab = ({ profileData, onInputChange }) => {
  const [workExperiences, setWorkExperiences] = useState(
    Array.isArray(profileData.work_experience) ? profileData.work_experience : []
  );
  const [expandedForm, setExpandedForm] = useState(false);
  
  // Sync with profileData when it changes
  useEffect(() => {
    if (Array.isArray(profileData.work_experience)) {
      setWorkExperiences([...profileData.work_experience]);
      console.log('WorkExperienceTab: Syncing with profileData:', profileData.work_experience);
    }
  }, [profileData.work_experience]);
  const [editIndex, setEditIndex] = useState(-1);
  const [newExperience, setNewExperience] = useState({
    position: '',
    company: '',
    location: '',
    type: 'Full-time',
    start_date: '',
    end_date: '',
    current: false,
    description: '',
    skills: [],
    achievements: [],
    responsibilities: []
  });
  const [newSkill, setNewSkill] = useState('');
  const [newAchievement, setNewAchievement] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');
  
  // Work experience types
  const workTypes = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship', 'Volunteer'];

  const handleExperienceChange = (field, value) => {
    setNewExperience(prevExperience => ({
      ...prevExperience,
      [field]: value
    }));
  };

  const handleAddSkill = () => {
    if (newSkill.trim()) {
      const sanitizedSkill = sanitizeSkillName(newSkill.trim());
      if (sanitizedSkill) {
        setNewExperience({
          ...newExperience,
          skills: [...newExperience.skills, sanitizedSkill]
        });
      }
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (index) => {
    const updatedSkills = [...newExperience.skills];
    updatedSkills.splice(index, 1);
    setNewExperience({
      ...newExperience,
      skills: updatedSkills
    });
  };

  const handleAddAchievement = () => {
    if (newAchievement.trim()) {
      setNewExperience({
        ...newExperience,
        achievements: [...newExperience.achievements, newAchievement.trim()]
      });
      setNewAchievement('');
    }
  };

  const handleRemoveAchievement = (index) => {
    const updatedAchievements = [...newExperience.achievements];
    updatedAchievements.splice(index, 1);
    setNewExperience({
      ...newExperience,
      achievements: updatedAchievements
    });
  };

  const handleAddResponsibility = () => {
    if (newResponsibility.trim()) {
      setNewExperience({
        ...newExperience,
        responsibilities: [...newExperience.responsibilities, newResponsibility.trim()]
      });
      setNewResponsibility('');
    }
  };

  const handleRemoveResponsibility = (index) => {
    const updatedResponsibilities = [...newExperience.responsibilities];
    updatedResponsibilities.splice(index, 1);
    setNewExperience({
      ...newExperience,
      responsibilities: updatedResponsibilities
    });
  };

  const handleCurrentJob = (checked) => {
    setNewExperience({
      ...newExperience,
      current: checked,
      end_date: checked ? '' : newExperience.end_date
    });
  };

  const handleSaveExperience = () => {
    try {
      // Create a deep clone of the current experiences to avoid reference issues
      let updatedExperiences = JSON.parse(JSON.stringify(workExperiences));
      
      // Create a simplified version of the experience object
      // with only essential properties to reduce payload size
      const simplifiedExperience = {
        position: newExperience.position,
        company: newExperience.company,
        location: newExperience.location || '',
        type: newExperience.type || 'Full-time',
        start_date: newExperience.start_date,
        end_date: newExperience.current ? '' : (newExperience.end_date || ''),
        current: !!newExperience.current,
        description: newExperience.description || '',
        skills: Array.isArray(newExperience.skills) ? newExperience.skills : [],
        achievements: Array.isArray(newExperience.achievements) ? newExperience.achievements : [],
        responsibilities: Array.isArray(newExperience.responsibilities) ? newExperience.responsibilities : []
      };
      
      if (editIndex >= 0) {
        // Update existing experience
        updatedExperiences[editIndex] = simplifiedExperience;
      } else {
        // Add new experience
        updatedExperiences.push(simplifiedExperience);
      }
      
      // Update local state
      setWorkExperiences(updatedExperiences);
      
      // Propagate to parent component to ensure saving
      // This is critical for the changes to persist
      onInputChange('work_experience', updatedExperiences);
      
      console.log('Saved work experience:', JSON.stringify(updatedExperiences));
      
      // Reset form
      setNewExperience({
        position: '',
        company: '',
        location: '',
        type: 'Full-time',
        start_date: '',
        end_date: '',
        current: false,
        description: '',
        skills: [],
        achievements: [],
        responsibilities: []
      });
      setExpandedForm(false);
      setEditIndex(-1);
    } catch (error) {
      console.error('Error saving work experience:', error);
    }
    // This section is now handled within the handleSaveExperience function
    // to ensure the form reset happens only after successful data processing
  };

  const handleEditExperience = (index) => {
    setEditIndex(index);
    setNewExperience(workExperiences[index]);
    setExpandedForm(true);
  };

  const handleDeleteExperience = (index) => {
    // Create a deep clone to avoid reference issues
    const updatedExperiences = JSON.parse(JSON.stringify(workExperiences));
    updatedExperiences.splice(index, 1);
    
    // Update local state
    setWorkExperiences(updatedExperiences);
    
    // Ensure parent component state is updated for saving
    onInputChange('work_experience', updatedExperiences);
    console.log('Deleted work experience at index:', index, 'Updated list:', JSON.stringify(updatedExperiences));
  };

  const cancelForm = () => {
    setExpandedForm(false);
    setEditIndex(-1);
    setNewExperience({
      position: '',
      company: '',
      location: '',
      type: 'Full-time',
      start_date: '',
      end_date: '',
      current: false,
      description: '',
      skills: [],
      achievements: [],
      responsibilities: []
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-1">Work Experience</h2>
        <p className="text-text-secondary text-sm">Add your work history to showcase your experience</p>
      </div>

      {/* Existing Experience List */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Your Experience</h3>
          {!expandedForm && (
            <Button
              variant="primary"
              iconName="Plus"
              onClick={() => setExpandedForm(true)}
              size="sm"
            >
              Add Experience
            </Button>
          )}
        </div>

        {workExperiences.length === 0 && !expandedForm ? (
          <div className="text-center py-8 bg-secondary-50 rounded-lg">
            <Icon name="Briefcase" size={40} className="text-secondary-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium mb-2">No work experience added yet</h3>
            <p className="text-text-secondary mb-4">Showcase your professional background by adding your work history</p>
            <Button
              variant="outline"
              iconName="Plus"
              onClick={() => setExpandedForm(true)}
            >
              Add Work Experience
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            {workExperiences.map((experience, index) => (
              <div 
                key={index} 
                className="border border-border rounded-lg p-4 hover:bg-secondary-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h4 className="font-semibold text-text-primary">{experience.position}</h4>
                    <p className="text-text-secondary">{experience.company}</p>
                    <div className="flex items-center gap-2 text-sm text-text-muted">
                      <span>{experience.location}</span>
                      <span className="inline-block w-1 h-1 bg-text-muted rounded-full"></span>
                      <span>{experience.type}</span>
                    </div>
                    <p className="text-text-secondary text-sm">
                      {formatDate(experience.start_date)} - {experience.current ? 'Present' : formatDate(experience.end_date)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      iconName="Edit"
                      onClick={() => handleEditExperience(index)}
                      size="sm"
                    />
                    <Button
                      variant="ghost"
                      iconName="Trash2"
                      onClick={() => handleDeleteExperience(index)}
                      size="sm"
                      className="text-error hover:bg-error-50"
                    />
                  </div>
                </div>
                
                {/* Brief description preview */}
                {experience.description && (
                  <p className="text-text-secondary text-sm mt-2 line-clamp-2">
                    {experience.description}
                  </p>
                )}
                
                {/* Skills tags */}
                {experience.skills && experience.skills.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {experience.skills.map((skill, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full"
                      >
                        {typeof skill === 'object' && skill !== null ? (skill.name || skill.skill || 'Unknown') : skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Experience Form */}
        {expandedForm && (
          <div className="border border-border rounded-lg p-6 bg-surface mt-6">
            <h3 className="text-lg font-semibold mb-4">
              {editIndex >= 0 ? 'Edit Experience' : 'Add Experience'}
            </h3>
            
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                  <Input
                  label="Job Title"
                  value={newExperience.position || ''}
                  onChange={(e) => handleExperienceChange('position', e.target.value)}
                  placeholder="e.g., Virtual Assistant"
                  required
                />
                
                <Input
                  label="Company/Organization"
                  value={newExperience.company || ''}
                  onChange={(e) => handleExperienceChange('company', e.target.value)}
                  placeholder="e.g., ABC Company"
                  required
                />
                
                <Input
                  label="Location"
                  value={newExperience.location || ''}
                  onChange={(e) => handleExperienceChange('location', e.target.value)}
                  placeholder="e.g., Remote, New York, US"
                />                <div>
                  <label className="block mb-2 text-text-secondary font-medium">Employment Type</label>
                  <select 
                    className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    value={newExperience.type}
                    onChange={(e) => handleExperienceChange('type', e.target.value)}
                  >
                    {workTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <Input
                  label="Start Date"
                  type="date"
                  value={newExperience.start_date || ''}
                  onChange={(e) => handleExperienceChange('start_date', e.target.value)}
                  required
                />
                
                {!newExperience.current && (
                  <Input
                    label="End Date"
                    type="date"
                    value={newExperience.end_date || ''}
                    onChange={(e) => handleExperienceChange('end_date', e.target.value)}
                    disabled={newExperience.current}
                  />
                )}
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="current-position"
                  checked={newExperience.current}
                  onChange={(e) => handleCurrentJob(e.target.checked)}
                  className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="current-position" className="text-sm text-text-secondary">
                  I am currently working in this position
                </label>
              </div>
              
              <div>
                <label className="block mb-2 text-text-secondary font-medium">Description</label>
                <textarea
                  value={newExperience.description || ''}
                  onChange={(e) => handleExperienceChange('description', e.target.value)}
                  placeholder="Describe your role, responsibilities, and notable achievements..."
                  className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  rows={4}
                />
              </div>
              
              {/* Skills section */}
              <div>
                <label className="block mb-2 text-text-secondary font-medium">Skills Used</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {newExperience.skills.map((skill, i) => (
                    <div key={i} className="flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary rounded-full">
                      <span className="text-sm">{typeof skill === 'object' && skill !== null ? (skill.name || skill.skill || 'Unknown') : skill}</span>
                      <button 
                        onClick={() => handleRemoveSkill(i)}
                        className="text-primary hover:text-primary-700"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="Add a skill"
                    className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                  />
                  <Button
                    variant="outline"
                    iconName="Plus"
                    onClick={handleAddSkill}
                    disabled={!newSkill.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Achievements section */}
              <div>
                <label className="block mb-2 text-text-secondary font-medium">Key Achievements</label>
                <div className="space-y-2 mb-3">
                  {newExperience.achievements.map((achievement, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-success-50 rounded-lg">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <div className="flex-1 text-sm text-text-secondary">{achievement}</div>
                      <button 
                        onClick={() => handleRemoveAchievement(i)}
                        className="text-text-muted hover:text-error"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newAchievement}
                    onChange={(e) => setNewAchievement(e.target.value)}
                    placeholder="e.g., Increased productivity by 20%"
                    className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddAchievement()}
                  />
                  <Button
                    variant="outline"
                    iconName="Plus"
                    onClick={handleAddAchievement}
                    disabled={!newAchievement.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Responsibilities section */}
              <div>
                <label className="block mb-2 text-text-secondary font-medium">Key Responsibilities</label>
                <div className="space-y-2 mb-3">
                  {newExperience.responsibilities.map((responsibility, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-secondary-50 rounded-lg">
                      <div className="w-1.5 h-1.5 rounded-full bg-secondary-400 mt-2 flex-shrink-0"></div>
                      <div className="flex-1 text-sm text-text-secondary">{responsibility}</div>
                      <button 
                        onClick={() => handleRemoveResponsibility(i)}
                        className="text-text-muted hover:text-error"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newResponsibility}
                    onChange={(e) => setNewResponsibility(e.target.value)}
                    placeholder="e.g., Managed client communications"
                    className="flex-1 px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddResponsibility()}
                  />
                  <Button
                    variant="outline"
                    iconName="Plus"
                    onClick={handleAddResponsibility}
                    disabled={!newResponsibility.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button 
                  variant="ghost" 
                  onClick={cancelForm}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary"
                  onClick={handleSaveExperience}
                  disabled={!newExperience.position || !newExperience.company || !newExperience.start_date}
                >
                  {editIndex >= 0 ? 'Update Experience' : 'Add Experience'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="text-sm text-text-muted pt-3">
        <p>💡 <span className="font-medium">Tip:</span> Start with your most recent experience. Be specific about your achievements with measurable results where possible.</p>
      </div>
    </div>
  );
};

export default WorkExperienceTab;

